import React from "react";
import {Card} from "react-bootstrap";
import {Area, AreaChart, ResponsiveContainer, Tooltip} from "recharts";
import {formatEuro, formatEuroCompact, formatMonthPeriod, formatPercent, formatQuantity} from "./format.js";

/**
 * Eine Kachel je Sortiment (Übernummer): Name, die vier Kennzahlen und ein
 * kleines Flächendiagramm als Verlauf. Anklickbar - öffnet die Detailansicht
 * (siehe Sortimente.jsx). Die Farbe ist der Kachel fest zugeordnet (siehe
 * buildGroupColorMap in Sortimente.jsx) und wird NICHT zyklisch neu
 * vergeben, wenn mehr Sortimente als Palettenfarben angezeigt werden.
 */
export default function SortimentTile({row, color, metric, onSelect}) {
    const series = row.series || [];
    const valueLabel = metric === "quantity" ? formatQuantity(row.quantity) + " Stk." : formatEuro(row.net_amount);

    return (
        <Card
            role="button"
            tabIndex={0}
            className="shadow-sm h-100"
            style={{cursor: "pointer", borderTop: `3px solid ${color}`}}
            onClick={() => onSelect(row.parent_article_number)}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect(row.parent_article_number);
                }
            }}
        >
            <Card.Body className="d-flex flex-column gap-2">
                <div>
                    <div className="fw-semibold text-truncate" title={row.parent_name || row.parent_article_number}>
                        {row.parent_name || row.parent_article_number}
                    </div>
                    <div className="small text-muted text-truncate">
                        {row.parent_article_number} · {row.article_group || "ohne Warengruppe"}
                    </div>
                </div>

                <div style={{height: 60}}>
                    <ResponsiveContainer width="100%" height={60}>
                        <AreaChart data={series} margin={{top: 2, right: 2, left: 2, bottom: 2}}>
                            <defs>
                                <linearGradient id={`sortiment-fill-${row.parent_article_number}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={color} stopOpacity={0.4} />
                                    <stop offset="95%" stopColor={color} stopOpacity={0.03} />
                                </linearGradient>
                            </defs>
                            <Tooltip
                                formatter={(value) => [metric === "quantity" ? `${formatQuantity(value)} Stk.` : formatEuro(value), metric === "quantity" ? "Menge" : "Nettoumsatz"]}
                                labelFormatter={(label) => formatMonthPeriod(label)}
                                contentStyle={{fontSize: 12}}
                            />
                            <Area
                                type="monotone"
                                dataKey={metric}
                                stroke={color}
                                strokeWidth={2}
                                fill={`url(#sortiment-fill-${row.parent_article_number})`}
                                dot={false}
                                isAnimationActive={false}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>

                <div className="row g-2 small">
                    <div className="col-6">
                        <div className="text-muted">Menge</div>
                        <div className="fw-semibold">{formatQuantity(row.quantity)} Stk.</div>
                    </div>
                    <div className="col-6">
                        <div className="text-muted">Umsatz</div>
                        <div className="fw-semibold">{formatEuroCompact(row.net_amount)}</div>
                    </div>
                    <div className="col-6">
                        <div className="text-muted">Varianten</div>
                        <div className="fw-semibold">{formatQuantity(row.variant_count)}</div>
                    </div>
                    <div className="col-6">
                        <div className="text-muted">Umsatzanteil</div>
                        <div className="fw-semibold">{formatPercent(row.net_share_pct)}</div>
                    </div>
                </div>

                <div className="small text-muted">{valueLabel} im gewählten Zeitraum</div>
            </Card.Body>
        </Card>
    );
}
